import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { Entry, Fixture, Prediction, Round } from '@h2h/shared';
import { useAuth } from '../../context/AuthContext';
import { useHaptics } from '../../hooks/useHaptics';
import {
  useLeagues,
  useCurrentRound,
  useRoundFixtures,
  useMyEntry,
  useFixtureInsight
} from '../../hooks/useQueries';
import { identityFor } from '../../data/leagues';
import type { FixtureInsight } from '../../lib/api/insight';
import { cyclingDuelHref, footballFixtureHref, footballHubPickHref } from '../../lib/fixtureRoute';
import { CyclingDuelDetail } from './CyclingDuelDetail';
import { FootballMatchView } from './FootballMatchView';
import { MatchNotFound } from './MatchNotFound';
import { useFootballMatchDetailVM } from './useFootballMatchDetail';

function FootballMatchDetailRoute({
  fixture,
  fixtures,
  entry,
  round,
  leagueName,
  myPick,
  nationalTeams,
  insight,
  leagueId,
  roundId,
  onBack
}: {
  fixture: Fixture;
  fixtures: Fixture[];
  entry: Entry | undefined;
  round: Round | undefined;
  leagueName: string;
  myPick: Prediction | undefined;
  nationalTeams: boolean;
  insight: FixtureInsight | undefined;
  leagueId: string;
  roundId: string | null;
  onBack: () => void;
}) {
  const navigate = useNavigate();
  const { impact } = useHaptics();

  const goPicks = useCallback(() => {
    impact('medium');
    navigate(footballHubPickHref(leagueId, { roundId, fixtureId: fixture.fixtureId }));
  }, [impact, leagueId, navigate, roundId, fixture.fixtureId]);

  const goCompete = useCallback(() => {
    impact('light');
    navigate(`/play/${leagueId}`);
  }, [impact, leagueId, navigate]);

  const goLiveBoard = useCallback(() => {
    impact('light');
    navigate(`/play/${leagueId}/waiting${roundId ? `?round=${roundId}` : ''}`);
  }, [impact, leagueId, navigate, roundId]);

  const vm = useFootballMatchDetailVM({
    fixture,
    fixtures,
    entry,
    round,
    myPick,
    nationalTeams,
    goPicks,
    goCompete
  });

  return (
    <FootballMatchView
      fixture={fixture}
      round={round}
      leagueName={leagueName}
      myPick={myPick}
      vm={vm}
      insight={insight}
      onBack={onBack}
      onLiveBoard={goLiveBoard}
    />
  );
}

export default function MatchDetailScreen() {
  const navigate = useNavigate();
  const { impact } = useHaptics();
  const { leagueId, fixtureId } = useParams<{ leagueId: string; fixtureId: string }>();
  const [params] = useSearchParams();
  const { uid } = useAuth();

  const { data: leagues } = useLeagues();
  const league = leagues?.find((l) => l.leagueId === leagueId);
  const roundId = params.get('round') ?? league?.currentRoundId ?? null;

  const { data: round } = useCurrentRound(leagueId, roundId);
  const { data: fixtures } = useRoundFixtures(leagueId, roundId, true);
  const { data: entry } = useMyEntry(leagueId, roundId, uid);
  const { data: insight } = useFixtureInsight(leagueId, roundId, fixtureId);

  const fixture = useMemo<Fixture | undefined>(
    () => (fixtures ?? []).find((f) => f.fixtureId === fixtureId),
    [fixtures, fixtureId]
  );
  const myPick = useMemo<Prediction | undefined>(
    () => entry?.predictions.find((p) => p.fixtureId === fixtureId),
    [entry, fixtureId]
  );

  const leagueName = league?.name ?? identityFor(leagueId ?? '')?.name ?? 'League';
  const nationalTeams = league?.country === 'World';
  const isCycling = league?.sport === 'CYCLING';
  const isCyclingFixture = isCycling || !!fixture?.riderA;

  const goBack = useCallback(() => {
    impact('light');
    navigate(-1);
  }, [impact, navigate]);

  const goCyclingPicks = useCallback(() => {
    impact('medium');
    navigate(`/play/${leagueId}${roundId ? `?round=${roundId}` : ''}`);
  }, [impact, leagueId, navigate, roundId]);

  const goCyclingBoard = useCallback(() => {
    impact('light');
    navigate(`/play/${leagueId}/waiting${roundId ? `?round=${roundId}` : ''}`);
  }, [impact, leagueId, navigate, roundId]);

  // Pre-kickoff with no card — skip this interstitial and open the builder directly.
  useEffect(() => {
    if (!fixture || !leagueId) return;
    if (isCyclingFixture) {
      const target = cyclingDuelHref(leagueId, fixture, {
        roundId,
        round: round ?? null,
        entry: entry ?? null
      });
      if (target.includes('?duel=') || target.includes('&duel=')) navigate(target, { replace: true });
      return;
    }
    const target = footballFixtureHref(leagueId, fixture, {
      roundId,
      round: round ?? null,
      entry: entry ?? null,
      myPick
    });
    if (target.includes('?fixture=')) navigate(target, { replace: true });
  }, [fixture, leagueId, roundId, round, entry, myPick, isCyclingFixture, navigate]);

  if (!fixture) {
    return <MatchNotFound isCycling={isCycling} onBack={goBack} />;
  }

  // Cycling duels reuse the fixture doc but carry rider refs + a binary verdict, so
  // they get a dedicated, simpler detail (no team markets / live timeline / stats).
  if (isCyclingFixture) {
    return (
      <CyclingDuelDetail
        fixture={fixture}
        fixtures={fixtures ?? []}
        entry={entry ?? null}
        round={round ?? null}
        leagueName={leagueName}
        onBack={goBack}
        onPicks={goCyclingPicks}
        onBoard={goCyclingBoard}
      />
    );
  }

  return (
    <FootballMatchDetailRoute
      fixture={fixture}
      fixtures={fixtures ?? []}
      entry={entry ?? undefined}
      round={round ?? undefined}
      leagueName={leagueName}
      myPick={myPick}
      nationalTeams={nationalTeams}
      insight={insight}
      leagueId={leagueId!}
      roundId={roundId}
      onBack={goBack}
    />
  );
}
